import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { useToastOptional } from "@/components/Toast/Toast";
import { restoreFromTrash } from "@/lib/api/handlers/GuestTrashHandling";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import { useGuestTrashUndo, type GuestTrashUndoEntry } from "@/lib/guests/guestTrashUndo";
import { invalidateAfterGuestTrashChange } from "@/lib/guests/guestTrashInvalidate";

/**
 * The pill after a trash delete: "N guests moved to the trash · Undo", 15 s,
 * then gone (`SlottingUndoBar` pattern). Undo = `restore {batchId}` with the
 * companions. A failed undo stays on screen as a sentence with the Papierkorb
 * link — never a blocking dialog, the delete already happened and the trash
 * page is where it can be retried. A TRANSIENT failure (409
 * `trashBatchStaging`: Phase B of the delete still running) keeps the Undo
 * button, because its sentence says "try again" (audit-B M4).
 *
 * Mounted once per event in `EventLayoutShell`, so the pill survives a page
 * change inside the event (delete in the list, undo from the dashboard).
 */
export function GuestTrashUndoBar({ eventId }: { eventId: string }) {
  // The HOST is hookless beyond the store read (zustand needs no provider):
  // it is mounted on every event page, including by hosts and tests that wrap
  // no intl / query / toast context, and must cost nothing while idle. The
  // pill below mounts only once a delete armed it — and stays mounted (keyed
  // on the batch) while its failure sentence is on screen.
  const pending = useGuestTrashUndo((s) => s.pending);
  const entry = pending && pending.eventId === eventId ? pending : null;
  if (!entry) return null;
  return <GuestTrashUndoPill key={entry.seq} eventId={eventId} entry={entry} />;
}

/** Codes whose sentence asks for a retry — the Undo button stays for it. */
const RETRYABLE_CODES: ReadonlySet<string> = new Set(["trashBatchStaging"]);

type Failure = { text: string; retryable: boolean };

function GuestTrashUndoPill({ eventId, entry }: { eventId: string; entry: GuestTrashUndoEntry }) {
  const t = useTranslations("guestTrash");
  const tErrors = useTranslations("errors");
  const clear = useGuestTrashUndo((s) => s.clear);
  // Optional: a host without a ToastProvider gets a silent success.
  const { showToast } = useToastOptional();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<Failure | null>(null);
  const seq = entry.seq;

  // One timer per pill, keyed on `seq`: a new delete inside the window makes a
  // new pill with a fresh 15 s, and the old timer clears only the old one. A
  // pill showing its failure sentence is not timed out from under the reader
  // — a retryable one included: the user was told to try again.
  useEffect(() => {
    if (failed) return;
    const remaining = Math.max(0, entry.expiresAt - Date.now());
    const timer = window.setTimeout(() => clear(seq), remaining);
    return () => window.clearTimeout(timer);
  }, [seq, entry.expiresAt, failed, clear]);

  const undo = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await restoreFromTrash(eventId, { batchId: entry.batchId, includeCompanions: true });
      clear(seq);
      showToast(t("undoRestored", { count: result.restored }), "success");
      invalidateAfterGuestTrashChange(queryClient, eventId);
    } catch (err) {
      const code = typeof (err as { message?: unknown })?.message === "string" ? (err as { message: string }).message : "";
      setFailed({
        text: translateBackendError(tErrors, err as Error, t("undoFailed")),
        retryable: RETRYABLE_CODES.has(code),
      });
    } finally {
      setBusy(false);
    }
  }, [entry.batchId, seq, busy, eventId, clear, showToast, t, tErrors, queryClient]);

  const undoButton = (
    <PrimaryButton
      text={busy ? t("undoRestoring") : t("undoButton")}
      onClick={() => void undo()}
      disabled={busy}
      size="xsmall"
      width="w-auto"
      testId="guest-trash-undo-button"
    />
  );

  return (
    // Same shell as SlottingUndoBar: shifts left with the open AI rail, the
    // outer strip is pointer-events-none so only the pill is interactive.
    <div
      className="fixed bottom-6 right-6 sm:right-[calc(1.5rem+var(--ai-rail-push))] transition-[right] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none z-[56] flex justify-end pointer-events-none"
      data-testid="guest-trash-undo-toast"
    >
      <div
        className="pointer-events-auto max-w-[min(24rem,calc(100vw-3rem))] flex items-center gap-3 px-4 py-2.5 rounded-lg shadow-lg border outline-primary surface-primary animate-in slide-in-from-bottom-4 fade-in duration-200"
        role="status"
        aria-live="polite"
      >
        {failed ? (
          <>
            <span className="body-3-regular font-error min-w-0 break-words" data-testid="guest-trash-undo-error">
              {failed.text}
            </span>
            {failed.retryable && undoButton}
            <Link
              to={`/event/${eventId}/guests/trash`}
              className="body-3-medium font-primary underline shrink-0"
              data-testid="guest-trash-undo-open-trash"
              onClick={() => clear(seq)}
            >
              {t("openTrash")}
            </Link>
          </>
        ) : (
          <>
            <span className="body-3-regular font-secondary min-w-0 break-words" data-testid="guest-trash-undo-label">
              {t("undoLabel", { count: entry.count })}
            </span>
            {undoButton}
          </>
        )}
        <button
          type="button"
          onClick={() => clear(seq)}
          className="ml-1 p-1 rounded-lg font-tertiary hover:font-primary hover:surface-secondary transition-colors"
          aria-label={t("dismiss")}
          title={t("dismiss")}
          data-testid="guest-trash-undo-dismiss"
        >
          <IconX size={16} />
        </button>
      </div>
    </div>
  );
}
